import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import {Address, SpatialCoordinates} from 'smag-commons/cfg/hierarchy';
import {MapsAPILoader} from '@agm/core';
import {isDefined} from '../../commons/commons';
import {FormControl} from '@angular/forms';
import {GoogleAddressComponent, parsePlaceResult} from 'smag-commons/fcn';

export interface AddressExt extends Address {
    coordinates: SpatialCoordinates;
    isValid: boolean;
    errors: string[];
}

@Component({
    selector: 'app-google-maps-address-autocomplete',
    templateUrl: './google-maps-address-autocomplete.component.html',
    styleUrls: ['./google-maps-address-autocomplete.component.scss']
})
export class GoogleMapsAddressAutocompleteComponent implements AfterViewInit, OnDestroy {
    private autocomplete: google.maps.places.Autocomplete = null;
    private autocompleteListener: google.maps.MapsEventListener = null;
    address: AddressExt = null;

    @ViewChild('search', {read: ElementRef}) private searchElementRef: ElementRef;
    @Input() formControlRef: FormControl;
    @Input() isRequired = true; // is address input required
    @Input() restrictSearch = false; // restrict the search area to Italy
    // the address could be a Google place with coordinates, or it could just be a string
    @Input() requiresGooglePlace = true; // false if the autocomplete is used only as a shortcut to fill the address input
    @Output() addressChange: EventEmitter<AddressExt> = new EventEmitter<AddressExt>();

    readonly isDefined = isDefined;

    constructor(private mapsAPILoader: MapsAPILoader,
                private ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngAfterViewInit() {
        this.initFormControl();
        this.addSearchListener();
        this.mapsAPILoader.load().then(() => this.setAddress());
    }

    ngOnDestroy() {
        this.autocomplete?.unbindAll();
        this.autocompleteListener?.remove();
    }

    loseFocus() {
        google.maps.event.trigger(this.searchElementRef.nativeElement, 'focus', {});
        google.maps.event.trigger(this.searchElementRef.nativeElement, 'keydown', {keyCode: 13});
    }

    private initFormControl() {
        if (this.formControlRef) {
            this.searchElementRef.nativeElement.value = this.formControlRef.value;
        }
    }

    private addSearchListener() {
        this.searchElementRef.nativeElement.addEventListener('change', () => {
            if (this.searchElementRef.nativeElement.value === '') {
                this.address = {
                    street: null,
                    streetNumber: null,
                    postalCode: null,
                    locality: null,
                    municipality: null,
                    province: null,
                    region: null,
                    country: null,
                    coordinates: null,
                    isValid: !this.isRequired,
                    errors: this.isRequired ? ['Missing address'] : []
                };
                this.setFormErrors();
                this.formControlRef.setValue(null);
            }
        });
    }

    private validate(address: AddressExt): string[] {
        const errors: string[] = [];
        if (isEmpty(address.municipality) && isEmpty(address.locality)) {
            errors.push('Missing municipality');
        }
        if (isEmpty(address.province)) {
            errors.push('Missing province');
        }
        if (isEmpty(address.region)) {
            errors.push('Missing region');
        }
        if (!address.coordinates.latitude) {
            errors.push('Missing latitude');
        }
        if (!address.coordinates.longitude) {
            errors.push('Missing longitude');
        }
        return errors;
    }

    private initAddress() {
        this.address = {
            street: null,
            streetNumber: null,
            postalCode: null,
            locality: null,
            municipality: null,
            province: null,
            region: null,
            country: null,
            coordinates: null,
            isValid: false,
            errors: []
        } as AddressExt;
    }


    private setAddress() {

        if (this.restrictSearch) {
            this.autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement,
                {componentRestrictions: {country: 'it'}, types: ['geocode']});
        } else {
            this.autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement,
                {types: ['geocode']});
        }
        this.autocompleteListener = this.autocomplete.addListener('place_changed', () => {
            this.ngZone.run(() => {
                const place = this.autocomplete.getPlace();
                this.initAddress();
                if (!this.isPlaceValid(place)) {
                    return;
                }
                this.changeDetectorRef.markForCheck();
                if (!this.requiresGooglePlace) {
                    const parsedAddress = parsePlaceResult(isDefined(place.address_components) ? place.address_components as GoogleAddressComponent[] : [], this.searchElementRef.nativeElement.value);
                    this.address = fillAddress(parsedAddress);
                    if (!!place.geometry) {
                        this.address.coordinates = {
                            latitude: place.geometry.location.lat(),
                            longitude: place.geometry.location.lng()
                        };
                        this.formControlRef?.setValue(place.formatted_address);
                    }
                } else if (this.requiresGooglePlace) {
                    const parsedAddress = parsePlaceResult(isDefined(place.address_components) ? place.address_components as GoogleAddressComponent[] : [], this.searchElementRef.nativeElement.value);
                    this.address = fillAddress(parsedAddress);
                    this.address.coordinates = {
                        latitude: place.geometry.location.lat(),
                        longitude: place.geometry.location.lng()
                    };
                    this.address.errors = this.validate(this.address);
                    this.formControlRef?.setValue(place.formatted_address);
                }
                this.address.isValid = this.address.errors.length === 0;
                this.setFormErrors();
                this.addressChange.next(this.address);
            });
        });
    }

    private isPlaceValid(place: google.maps.places.PlaceResult): boolean {
        if (!this.requiresGooglePlace || isDefined(place.geometry)) {
            return true;
        }
        if (place.name === '') {
            if (this.isRequired) {
                this.address.errors = ['Missing address'];
                this.address.isValid = false;
            } else {
                this.address.errors = [];
                this.address.isValid = true;
            }
        } else {
            this.address.errors.push('Invalid address');
            this.address.isValid = false;
        }
        this.setFormErrors();
        this.formControlRef.setValue(place.formatted_address);
        this.addressChange.next(this.address);
        return false;
    }

    private setFormErrors() {
        this.formControlRef.setErrors(!this.address.isValid ? {invalid: true} : null);
    }
}


function isEmpty(s: string): boolean {
    return !s || s.trim().length === 0;
}

function fillAddress(parsedAddress: Address): AddressExt {
    return {
        street: parsedAddress.street,
        streetNumber: parsedAddress.streetNumber,
        postalCode: parsedAddress.postalCode,
        locality: parsedAddress.locality,
        municipality: parsedAddress.municipality,
        province: parsedAddress.province,
        region: parsedAddress.region,
        country: parsedAddress.country,
        coordinates: null,
        isValid: false,
        errors: [],
    } as AddressExt;
}

const provincesAndZones: { [province: string]: string } = {
    'AG': 'SICI',
    'AL': 'NORD',
    'AN': 'CNOR',
    'AO': 'NORD',
    'AR': 'CNOR',
    'AP': 'CNOR',
    'AT': 'NORD',
    'AV': 'CSUD',
    'BA': 'SUD',
    'BT': 'SUD',
    'BL': 'NORD',
    'BN': 'CSUD',
    'BG': 'NORD',
    'BI': 'NORD',
    'BO': 'NORD',
    'BZ': 'NORD',
    'BS': 'NORD',
    'BR': 'SUD',
    'CA': 'SARD',
    'CL': 'SICI',
    'CB': 'SUD',
    'CE': 'CSUD',
    'CT': 'SICI',
    'CZ': 'CALA',
    'CH': 'CSUD',
    'CO': 'NORD',
    'CS': 'CALA',
    'CR': 'NORD',
    'KR': 'CALA',
    'CN': 'NORD',
    'EN': 'SICI',
    'FM': 'CNOR',
    'FE': 'NORD',
    'FI': 'CNOR',
    'FG': 'SUD',
    'FC': 'NORD',
    'FR': 'CSUD',
    'GE': 'NORD',
    'GO': 'NORD',
    'GR': 'CNOR',
    'IM': 'NORD',
    'IS': 'SUD',
    'AQ': 'CSUD',
    'SP': 'NORD',
    'LT': 'CSUD',
    'LE': 'SUD',
    'LC': 'NORD',
    'LI': 'CNOR',
    'LO': 'NORD',
    'LU': 'CNOR',
    'MC': 'CNOR',
    'MN': 'NORD',
    'MS': 'CNOR',
    'MT': 'SUD',
    'ME': 'SICI',
    'MI': 'NORD',
    'MO': 'NORD',
    'MB': 'NORD',
    'NA': 'CSUD',
    'NO': 'NORD',
    'NU': 'SARD',
    'OR': 'SARD',
    'PD': 'NORD',
    'PA': 'SICI',
    'PR': 'NORD',
    'PV': 'NORD',
    'PG': 'CSUD',
    'PU': 'CNOR',
    'PE': 'CSUD',
    'PC': 'NORD',
    'PI': 'CNOR',
    'PT': 'CNOR',
    'PN': 'NORD',
    'PZ': 'SUD',
    'PO': 'CNOR',
    'RG': 'SICI',
    'RA': 'NORD',
    'RC': 'CALA',
    'RE': 'NORD',
    'RI': 'CSUD',
    'RN': 'NORD',
    'RM': 'CSUD',
    'RO': 'NORD',
    'SA': 'CSUD',
    'SS': 'SARD',
    'SV': 'NORD',
    'SI': 'CNOR',
    'SR': 'SICI',
    'SO': 'NORD',
    'SU': 'SARD',
    'TA': 'SUD',
    'TE': 'CSUD',
    'TR': 'CSUD',
    'TO': 'NORD',
    'TP': 'SICI',
    'TN': 'NORD',
    'TV': 'NORD',
    'TS': 'NORD',
    'UD': 'NORD',
    'VA': 'NORD',
    'VE': 'NORD',
    'VB': 'NORD',
    'VC': 'NORD',
    'VR': 'NORD',
    'VV': 'CALA',
    'VI': 'NORD',
    'VT': 'CSUD'
};

export function getZoneFromProvince(province: string): string {
    return provincesAndZones[province] ?? 'NORD';
}